import {
	addDoc,
	collection,
	collectionGroup,
	deleteDoc,
	doc,
	getDoc,
	getDocs,
	onSnapshot,
	orderBy,
	query,
	serverTimestamp,
	setDoc,
	updateDoc,
	where,
	writeBatch,
	arrayUnion,
	arrayRemove,
	deleteField
} from "firebase/firestore";
import {
	getStorage,
	ref as storageRef,
	uploadBytes,
	getDownloadURL,
	deleteObject
} from "firebase/storage";

import { db } from "@/composables/events/config";

const TICKETS_COLLECTION = "support-tickets";
const NOTIFICATIONS_COLLECTION = "support-notifications";
const VISITS_COLLECTION = "support-visits";
const CONTACTS_COLLECTION = "support-contacts";
const ACTIVITY_SUBCOLLECTION = "activity";
const TODOS_SUBCOLLECTION = "todos";

// Storage uses the default Firebase app initialized in events/config.js
const storage = getStorage();

// --- Tickets ---

export const subscribeTickets = (onUpdate, onError) => {

	const q = query(
		collection(db, TICKETS_COLLECTION),
		orderBy("updatedAt", "desc")
	);

	return onSnapshot(
		q,
		(snap) => {
			const tickets = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

			onUpdate(tickets);
		},
		(err) => {
			console.error("[TICKETING] subscribeTickets error", err);
			onError?.(err);
		}
	);
};

export const subscribeTicket = (ticketId, onUpdate, onError) => {

	return onSnapshot(
		doc(db, TICKETS_COLLECTION, ticketId),
		(snap) => onUpdate(snap.exists() ? { id: snap.id, ...snap.data() } : null),
		(err) => {
			console.error("[TICKETING] subscribeTicket error", err);
			onError?.(err);
		}
	);
};

export const subscribeMessages = (ticketId, onUpdate, onError) => {

	const q = query(
		collection(db, TICKETS_COLLECTION, ticketId, "messages"),
		orderBy("createdAt", "asc")
	);

	return onSnapshot(
		q,
		(snap) => {
			const messages = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

			onUpdate(messages);
		},
		(err) => {
			console.error("[TICKETING] subscribeMessages error", err);
			onError?.(err);
		}
	);
};

export const createTicket = async (data, currentUser) => {

	const now = serverTimestamp();
	// The ticket creator is implicitly a watcher so they receive notifications
	// like any other watcher (and can opt out by removing themselves).
	const initialWatcherIds = Array.from(new Set([
		...(data.watcherIds ?? []),
		currentUser.id
	].filter(Boolean)));
	// New tickets are always written with the multi-org `organizations` array.
	// The legacy organizationId/organizationName remain populated (and point to
	// the primary org = first entry) for backward compatibility with code/queries
	// that still read them.
	const organizations = normalizeOrgsForWrite(data);
	const primary = organizations[0] ?? null;
	const ref = await addDoc(collection(db, TICKETS_COLLECTION), {
		...data,
		organizations,
		organizationId: primary?.id ?? data.organizationId ?? null,
		organizationName: primary?.name ?? data.organizationName ?? "",
		watcherIds: initialWatcherIds,
		assignedDevopsIds: data.assignedDevopsIds ?? [],
		createdAt: now,
		updatedAt: now,
		createdById: currentUser.id,
		createdByName: currentUser.fullName
	});

	await writeActivity(ref.id, {
		type: "created",
		actorId: currentUser.id,
		actorName: currentUser.fullName
	});

	const recipients = uniqueRecipients([
		...initialWatcherIds,
		...(data.assignedDevopsIds ?? [])
	], currentUser.id);

	await Promise.all(
		recipients.map((uid) =>
			pushNotification(uid, {
				ticketId: ref.id,
				ticketTitle: data.title,
				organizationName: data.organizationName,
				type: "ticket_created",
				message: `${currentUser.fullName} created ticket "${data.title}"`,
				actorId: currentUser.id,
				actorName: currentUser.fullName
			})
		)
	);

	return ref.id;
};

export const updateTicket = async (ticketId, patch, currentUser, previous = null) => {

	const before = previous ?? (await getDoc(doc(db, TICKETS_COLLECTION, ticketId))).data() ?? {};

	// Stamp resolved/closed timestamps when the status transitions into those
	// states so the ticket list can show "Resolved on / Closed on" without
	// having to load the per-ticket activity log.
	const augmentedPatch = { ...patch };

	if (Object.prototype.hasOwnProperty.call(patch, "status") && patch.status !== before.status) {
		if (patch.status === "resolved") augmentedPatch.resolvedAt = serverTimestamp();
		if (patch.status === "closed") augmentedPatch.closedAt = serverTimestamp();
	}

	await updateDoc(doc(db, TICKETS_COLLECTION, ticketId), {
		...augmentedPatch,
		updatedAt: serverTimestamp()
	});

	// Write per-field activity entries for tracked fields
	const diffs = computeFieldDiffs(before, patch);

	await Promise.all(
		diffs.map((d) =>
			writeActivity(ticketId, {
				type: d.type ?? "updated",
				field: d.field,
				fromValue: d.from,
				toValue: d.to,
				actorId: currentUser?.id,
				actorName: currentUser?.fullName
			})
		)
	);

	// Notify watchers + assignees (creator is implicitly a watcher)
	const snap = await getDoc(doc(db, TICKETS_COLLECTION, ticketId));

	if (!snap.exists()) return;

	const t = snap.data();
	const recipients = uniqueRecipients([
		...(t.watcherIds ?? []),
		...(t.assignedDevopsIds ?? [])
	], currentUser?.id);

	const actorName = currentUser?.fullName ?? "Someone";

	if (diffs.length) {
		await Promise.all(
			recipients.flatMap((uid) =>
				diffs.map((d) => {
					const notifType = d.type === "attachments-added"
						? "attachments_added"
						: d.type === "attachments-removed"
							? "attachments_removed"
							: "ticket_updated";
					const message = d.type === "attachments-added"
						? `${actorName} added attachment(s): ${d.to}`
						: d.type === "attachments-removed"
							? `${actorName} removed attachment(s): ${d.to}`
							: `${actorName} changed ${d.field}`;

					return pushNotification(uid, {
						ticketId,
						ticketTitle: t.title,
						organizationName: t.organizationName,
						type: notifType,
						field: d.field,
						fromValue: d.from,
						toValue: d.to,
						message,
						actorId: currentUser?.id,
						actorName: currentUser?.fullName
					});
				})
			)
		);
	} else {
		const summary = patchSummary(patch);

		await Promise.all(
			recipients.map((uid) =>
				pushNotification(uid, {
					ticketId,
					ticketTitle: t.title,
					organizationName: t.organizationName,
					type: "ticket_updated",
					message: `${actorName} updated ${summary}`,
					actorId: currentUser?.id,
					actorName: currentUser?.fullName
				})
			)
		);
	}
};

export const addWatcher = async (ticketId, userId, currentUser, userName = null) => {
	const beforeSnap = await getDoc(doc(db, TICKETS_COLLECTION, ticketId));
	const beforeWatchers = beforeSnap.exists() ? (beforeSnap.data().watcherIds ?? []) : [];

	if (beforeWatchers.includes(userId)) return;

	await updateDoc(doc(db, TICKETS_COLLECTION, ticketId), {
		watcherIds: arrayUnion(userId),
		updatedAt: serverTimestamp()
	});

	const isSelf = currentUser && userId === currentUser.id;
	const resolvedName = userName
		|| (isSelf ? currentUser?.fullName : null)
		|| "Someone";

	await writeActivity(ticketId, {
		type: "watcher-added",
		field: "watchers",
		toValue: resolvedName,
		actorId: currentUser?.id,
		actorName: currentUser?.fullName
	});

	const t = beforeSnap.exists() ? beforeSnap.data() : {};
	const actorName = currentUser?.fullName ?? "Someone";

	// Notify the added watcher (if added by someone else)
	if (currentUser && userId !== currentUser.id) {
		await pushNotification(userId, {
			ticketId,
			ticketTitle: t.title,
			organizationName: t.organizationName,
			type: "added_as_watcher",
			message: `${actorName} added you as a watcher`,
			actorId: currentUser.id,
			actorName: currentUser.fullName
		});
	}

	// Notify the rest of the team
	const recipients = uniqueRecipients([
		...beforeWatchers,
		...(t.assignedDevopsIds ?? [])
	], currentUser?.id).filter((uid) => uid !== userId);

	await Promise.all(
		recipients.map((uid) =>
			pushNotification(uid, {
				ticketId,
				ticketTitle: t.title,
				organizationName: t.organizationName,
				type: "watcher_added",
				field: "watchers",
				toValue: resolvedName,
				message: `${actorName} added ${resolvedName} as a watcher`,
				actorId: currentUser?.id,
				actorName: currentUser?.fullName
			})
		)
	);
};

export const removeWatcher = async (ticketId, userId, currentUser = null, userName = null) => {
	const beforeSnap = await getDoc(doc(db, TICKETS_COLLECTION, ticketId));
	const beforeWatchers = beforeSnap.exists() ? (beforeSnap.data().watcherIds ?? []) : [];

	if (!beforeWatchers.includes(userId)) return;

	await updateDoc(doc(db, TICKETS_COLLECTION, ticketId), {
		watcherIds: arrayRemove(userId),
		updatedAt: serverTimestamp()
	});

	const isSelf = currentUser && userId === currentUser.id;
	const resolvedName = userName
		|| (isSelf ? currentUser?.fullName : null)
		|| "Someone";

	await writeActivity(ticketId, {
		type: "watcher-removed",
		field: "watchers",
		toValue: resolvedName,
		actorId: currentUser?.id,
		actorName: currentUser?.fullName
	});

	const t = beforeSnap.exists() ? beforeSnap.data() : {};
	const actorName = currentUser?.fullName ?? "Someone";

	// Notify the removed user (if removed by someone else)
	if (currentUser && userId !== currentUser.id) {
		await pushNotification(userId, {
			ticketId,
			ticketTitle: t.title,
			organizationName: t.organizationName,
			type: "removed_as_watcher",
			message: `${actorName} removed you as a watcher`,
			actorId: currentUser.id,
			actorName: currentUser.fullName
		});
	}

	// Notify the rest of the team
	const recipients = uniqueRecipients([
		...beforeWatchers,
		...(t.assignedDevopsIds ?? [])
	], currentUser?.id).filter((uid) => uid !== userId);

	await Promise.all(
		recipients.map((uid) =>
			pushNotification(uid, {
				ticketId,
				ticketTitle: t.title,
				organizationName: t.organizationName,
				type: "watcher_removed",
				field: "watchers",
				toValue: resolvedName,
				message: `${actorName} removed ${resolvedName} as a watcher`,
				actorId: currentUser?.id,
				actorName: currentUser?.fullName
			})
		)
	);
};

// --- Multi-organization helpers ------------------------------------------
// Tickets historically stored a single organization via top-level fields
// (organizationId / organizationName + assignedUserId / assignedUserName).
// New tickets always include a richer `organizations` array where the first
// entry is the primary org (mirrored to the legacy top-level fields for
// backward compatibility). Each entry can carry its own optional contact
// (assignedUserId / assignedUserName / assignedUserEmail).

/**
 * Returns a normalized `Array<{id, name, assignedUserId?, assignedUserName?, assignedUserEmail?}>`
 * for a ticket regardless of whether it uses the legacy single-org shape or
 * the new multi-org `organizations` array.
 */
export const getTicketOrganizations = (t) => {
	if (Array.isArray(t?.organizations) && t.organizations.length) {
		return t.organizations.map((o) => ({
			id: o?.id ?? null,
			name: o?.name ?? "",
			assignedUserId: o?.assignedUserId ?? null,
			assignedUserName: o?.assignedUserName ?? null,
			assignedUserEmail: o?.assignedUserEmail ?? null
		}));
	}
	if (t?.organizationId || t?.organizationName) {
		return [{
			id: t.organizationId ?? null,
			name: t.organizationName ?? "",
			assignedUserId: t.assignedUserId ?? null,
			assignedUserName: t.assignedUserName ?? null,
			assignedUserEmail: t.assignedUserEmail ?? null
		}];
	}
	return [];
};

/**
 * Builds the `organizations` array that should be written to Firestore for a
 * new or updated ticket. Accepts either `data.organizations` (preferred) or
 * the legacy single-org `data.organizationId`/`data.organizationName` shape.
 */
const normalizeOrgsForWrite = (data) => {
	if (Array.isArray(data?.organizations) && data.organizations.length) {
		const seen = new Set();
		const out = [];

		for (const o of data.organizations) {
			if (!o || (!o.id && !o.name)) continue;
			const key = o.id ?? `name:${o.name}`;

			if (seen.has(key)) continue;
			seen.add(key);
			out.push({
				id: o.id ?? null,
				name: o.name ?? "",
				assignedUserId: o.assignedUserId ?? null,
				assignedUserName: o.assignedUserName ?? null,
				assignedUserEmail: o.assignedUserEmail ?? null
			});
		}

		return out;
	}
	if (data?.organizationId || data?.organizationName) {
		return [{
			id: data.organizationId ?? null,
			name: data.organizationName ?? "",
			assignedUserId: data.assignedUserId ?? null,
			assignedUserName: data.assignedUserName ?? null,
			assignedUserEmail: data.assignedUserEmail ?? null
		}];
	}

	return [];
};

/**
 * Links an additional organization to an existing ticket. If the ticket still
 * uses the legacy single-org shape (no `organizations` field), it is migrated
 * to the new shape on the fly with the existing primary org as the first entry.
 */
export const addTicketOrganization = async (ticketId, org, currentUser) => {
	if (!org || !org.id) return;

	const ref = doc(db, TICKETS_COLLECTION, ticketId);
	const snap = await getDoc(ref);

	if (!snap.exists()) return;

	const t = { id: snap.id, ...snap.data() };
	const current = getTicketOrganizations(t);

	if (current.some((o) => o.id === org.id)) return; // already linked

	const next = [
		...current,
		{
			id: org.id,
			name: org.name ?? "",
			assignedUserId: org.assignedUserId ?? null,
			assignedUserName: org.assignedUserName ?? null,
			assignedUserEmail: org.assignedUserEmail ?? null
		}
	];

	await updateDoc(ref, {
		organizations: next,
		updatedAt: serverTimestamp()
	});

	await writeActivity(ticketId, {
		type: "org-added",
		field: "organizations",
		toValue: org.name || org.id,
		actorId: currentUser?.id,
		actorName: currentUser?.fullName
	});

	const recipients = uniqueRecipients([
		...(t.watcherIds ?? []),
		...(t.assignedDevopsIds ?? [])
	], currentUser?.id);
	const actorName = currentUser?.fullName ?? "Someone";

	await Promise.all(
		recipients.map((uid) =>
			pushNotification(uid, {
				ticketId,
				ticketTitle: t.title,
				organizationName: t.organizationName,
				type: "org_added",
				field: "organizations",
				toValue: org.name || org.id,
				message: `${actorName} added organization "${org.name || org.id}"`,
				actorId: currentUser?.id,
				actorName: currentUser?.fullName
			})
		)
	);
};

/**
 * Removes an organization from a ticket. If the removed org was the legacy
 * primary, the next remaining org is promoted to the legacy top-level fields
 * (organizationId / organizationName / assignedUserId / assignedUserName /
 * assignedUserEmail) so backward-compatible code keeps working.
 */
export const removeTicketOrganization = async (ticketId, orgId, currentUser) => {
	if (!orgId) return;

	const ref = doc(db, TICKETS_COLLECTION, ticketId);
	const snap = await getDoc(ref);

	if (!snap.exists()) return;

	const t = { id: snap.id, ...snap.data() };
	const current = getTicketOrganizations(t);
	const target = current.find((o) => o.id === orgId);

	if (!target) return;

	const next = current.filter((o) => o.id !== orgId);
	const patch = {
		organizations: next,
		updatedAt: serverTimestamp()
	};

	if (t.organizationId === orgId) {
		const promoted = next[0] ?? null;

		patch.organizationId = promoted?.id ?? null;
		patch.organizationName = promoted?.name ?? "";
		patch.assignedUserId = promoted?.assignedUserId ?? null;
		patch.assignedUserName = promoted?.assignedUserName ?? null;
		patch.assignedUserEmail = promoted?.assignedUserEmail ?? null;
	}

	await updateDoc(ref, patch);

	await writeActivity(ticketId, {
		type: "org-removed",
		field: "organizations",
		toValue: target.name || target.id,
		actorId: currentUser?.id,
		actorName: currentUser?.fullName
	});

	const recipients = uniqueRecipients([
		...(t.watcherIds ?? []),
		...(t.assignedDevopsIds ?? [])
	], currentUser?.id);
	const actorName = currentUser?.fullName ?? "Someone";

	await Promise.all(
		recipients.map((uid) =>
			pushNotification(uid, {
				ticketId,
				ticketTitle: t.title,
				organizationName: t.organizationName,
				type: "org_removed",
				field: "organizations",
				toValue: target.name || target.id,
				message: `${actorName} removed organization "${target.name || target.id}"`,
				actorId: currentUser?.id,
				actorName: currentUser?.fullName
			})
		)
	);
};

/**
 * Updates the per-org assigned contact for a linked organization. Auto-migrates
 * legacy tickets to the new `organizations` shape if needed.
 */
export const updateTicketOrganizationContact = async (ticketId, orgId, contact, currentUser) => {
	if (!orgId) return;

	const ref = doc(db, TICKETS_COLLECTION, ticketId);
	const snap = await getDoc(ref);

	if (!snap.exists()) return;

	const t = { id: snap.id, ...snap.data() };
	const current = getTicketOrganizations(t);
	const idx = current.findIndex((o) => o.id === orgId);

	if (idx === -1) return;

	const next = current.map((o, i) => i === idx
		? {
			...o,
			assignedUserId: contact?.id ?? null,
			assignedUserName: contact?.fullName ?? null,
			assignedUserEmail: contact?.email ?? null
		}
		: o);
	const patch = {
		organizations: next,
		updatedAt: serverTimestamp()
	};

	// Keep legacy top-level assigned user in sync with the primary org's contact.
	if (t.organizationId === orgId) {
		patch.assignedUserId = contact?.id ?? null;
		patch.assignedUserName = contact?.fullName ?? null;
		patch.assignedUserEmail = contact?.email ?? null;
	}

	await updateDoc(ref, patch);

	await writeActivity(ticketId, {
		type: "org-contact-updated",
		field: "organizations",
		fromValue: current[idx].assignedUserName || null,
		toValue: contact?.fullName || null,
		actorId: currentUser?.id,
		actorName: currentUser?.fullName
	});

	const recipients = uniqueRecipients([
		...(t.watcherIds ?? []),
		...(t.assignedDevopsIds ?? [])
	], currentUser?.id);
	const actorName = currentUser?.fullName ?? "Someone";
	const orgName = current[idx].name || orgId;
	const fromName = current[idx].assignedUserName || null;
	const toName = contact?.fullName || null;
	const action = toName
		? (fromName ? `changed contact for "${orgName}" from "${fromName}" to "${toName}"` : `set contact for "${orgName}" to "${toName}"`)
		: `cleared contact for "${orgName}"`;

	await Promise.all(
		recipients.map((uid) =>
			pushNotification(uid, {
				ticketId,
				ticketTitle: t.title,
				organizationName: t.organizationName,
				type: "org_contact_updated",
				field: "organizations",
				fromValue: fromName,
				toValue: toName,
				message: `${actorName} ${action}`,
				actorId: currentUser?.id,
				actorName: currentUser?.fullName
			})
		)
	);
};

/**
 * Notifies the given devops user IDs that a ticket has no devops assigned
 * (either freshly created or just unassigned). Excludes the actor from the
 * recipient list to avoid self-notifications.
 */
export const notifyUnassignedTicket = async (ticketId, ticketTitle, devopsIds, currentUser, organizationName = null) => {
	if (!ticketId || !Array.isArray(devopsIds) || devopsIds.length === 0) return;

	const recipients = uniqueRecipients(devopsIds, currentUser?.id);

	if (!recipients.length) return;

	await Promise.all(
		recipients.map((uid) =>
			pushNotification(uid, {
				ticketId,
				ticketTitle,
				organizationName,
				type: "ticket_unassigned",
				message: "New unassigned ticket",
				actorId: currentUser?.id,
				actorName: currentUser?.fullName
			})
		)
	);
};

export const toggleReaction = async (ticketId, messageId, emoji, currentUser) => {

	const messageRef = doc(db, TICKETS_COLLECTION, ticketId, "messages", messageId);
	const snap = await getDoc(messageRef);

	if (!snap.exists()) return;

	const data = snap.data();
	const reactions = data.reactions ?? {};
	const already = reactions?.[emoji]?.[currentUser.id];
	const fieldKey = `reactions.${emoji}.${currentUser.id}`;

	if (already) {
		await updateDoc(messageRef, { [fieldKey]: deleteField() });
		return;
	}

	await updateDoc(messageRef, {
		[fieldKey]: {
			name: currentUser.fullName,
			at: new Date().toISOString()
		}
	});

	if (data.authorId && data.authorId !== currentUser.id) {
		const tSnap = await getDoc(doc(db, TICKETS_COLLECTION, ticketId));
		const t = tSnap.exists() ? tSnap.data() : {};

		await pushNotification(data.authorId, {
			ticketId,
			ticketTitle: t.title,
			organizationName: t.organizationName,
			messageId,
			type: "reaction",
			emoji,
			message: `${currentUser.fullName} reacted ${emoji} to: ${truncate(data.body || "", 80)}`,
			actorId: currentUser.id,
			actorName: currentUser.fullName
		});
	}
};

// Toggle an emoji reaction on a follow-up (stored as an activity document).
// Mirrors toggleReaction so follow-up bubbles in the conversation behave like
// message bubbles. Notifies the user who logged the follow-up.
export const toggleActivityReaction = async (ticketId, activityId, emoji, currentUser) => {
	if (!ticketId || !activityId || !emoji || !currentUser?.id) return;

	const activityRef = doc(db, TICKETS_COLLECTION, ticketId, ACTIVITY_SUBCOLLECTION, activityId);
	const snap = await getDoc(activityRef);

	if (!snap.exists()) return;

	const data = snap.data();
	const reactions = data.reactions ?? {};
	const already = reactions?.[emoji]?.[currentUser.id];
	const fieldKey = `reactions.${emoji}.${currentUser.id}`;

	if (already) {
		await updateDoc(activityRef, { [fieldKey]: deleteField() });
		return;
	}

	await updateDoc(activityRef, {
		[fieldKey]: {
			name: currentUser.fullName,
			at: new Date().toISOString()
		}
	});

	if (data.actorId && data.actorId !== currentUser.id) {
		const tSnap = await getDoc(doc(db, TICKETS_COLLECTION, ticketId));
		const t = tSnap.exists() ? tSnap.data() : {};

		await pushNotification(data.actorId, {
			ticketId,
			ticketTitle: t.title,
			organizationName: t.organizationName,
			type: "reaction",
			emoji,
			message: `${currentUser.fullName} reacted ${emoji} to your ${data.toValue ?? "follow-up"} follow-up`,
			actorId: currentUser.id,
			actorName: currentUser.fullName
		});
	}
};

export const addMessage = async (ticketId, body, mentions, currentUser, attachments = []) => {

	const now = serverTimestamp();
	const messagesRef = collection(db, TICKETS_COLLECTION, ticketId, "messages");

	await addDoc(messagesRef, {
		body,
		mentions: mentions ?? [],
		attachments: attachments ?? [],
		authorId: currentUser.id,
		authorName: currentUser.fullName,
		createdAt: now
	});

	const ticketUpdate = {
		updatedAt: now,
		lastMessageAt: now,
		lastMessageBy: currentUser.fullName
	};
	const allMentions = (mentions ?? []).filter(Boolean);

	if (allMentions.length) {
		ticketUpdate.mentionedUserIds = arrayUnion(...allMentions);
		// Per-user last mention timestamp so the list can flag unseen
		// mentions for the current user without scanning messages.
		allMentions.forEach((uid) => {
			ticketUpdate[`lastMentionByUser.${uid}`] = now;
		});
	}
	await updateDoc(doc(db, TICKETS_COLLECTION, ticketId), ticketUpdate);

	const snap = await getDoc(doc(db, TICKETS_COLLECTION, ticketId));

	if (!snap.exists()) return;

	const t = snap.data();

	const baseRecipients = uniqueRecipients([
		...(t.watcherIds ?? []),
		...(t.assignedDevopsIds ?? [])
	], currentUser.id);

	const mentionSet = new Set((mentions ?? []).filter((m) => m && m !== currentUser.id));

	// All recipients get a "new_message"; mentioned users get an extra "mention".
	await Promise.all([
		...baseRecipients.map((uid) =>
			pushNotification(uid, {
				ticketId,
				ticketTitle: t.title,
				organizationName: t.organizationName,
				type: "new_message",
				message: `${currentUser.fullName}: ${truncate(body, 80)}`,
				actorId: currentUser.id,
				actorName: currentUser.fullName
			})
		),
		...[...mentionSet].map((uid) =>
			pushNotification(uid, {
				ticketId,
				ticketTitle: t.title,
				organizationName: t.organizationName,
				type: "mention",
				message: `${currentUser.fullName} mentioned you: ${truncate(body, 80)}`,
				actorId: currentUser.id,
				actorName: currentUser.fullName
			})
		)
	]);
};

// --- Notifications ---

export const subscribeNotifications = (userId, onUpdate, onError) => {

	const q = query(
		collection(db, NOTIFICATIONS_COLLECTION, userId, "items"),
		orderBy("createdAt", "desc")
	);

	return onSnapshot(
		q,
		(snap) => {
			const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

			onUpdate(items);
		},
		(err) => {
			console.error("[TICKETING] subscribeNotifications error", err);
			onError?.(err);
		}
	);
};

const pushNotification = async (userId, payload) => {
	if (!userId) return;
	const ref = collection(db, NOTIFICATIONS_COLLECTION, userId, "items");

	await addDoc(ref, {
		...payload,
		read: false,
		createdAt: serverTimestamp()
	});
};

export const markNotificationRead = async (userId, notificationId) => {
	await updateDoc(
		doc(db, NOTIFICATIONS_COLLECTION, userId, "items", notificationId),
		{ read: true }
	);
};

export const markAllNotificationsRead = async (userId) => {
	const snap = await getDocs(
		query(
			collection(db, NOTIFICATIONS_COLLECTION, userId, "items"),
			where("read", "==", false)
		)
	);

	const batch = writeBatch(db);

	snap.docs.forEach((d) => batch.update(d.ref, { read: true }));
	await batch.commit();
};

// Mark every unread notification tied to a given ticket as read. Used when a
// user opens a ticket so its pending notifications clear automatically. The
// query is a single-field filter on `ticketId` (no composite index required);
// the read flag is filtered client-side.
export const markTicketNotificationsRead = async (userId, ticketId) => {
	if (!userId || !ticketId) return;
	const snap = await getDocs(
		query(
			collection(db, NOTIFICATIONS_COLLECTION, userId, "items"),
			where("ticketId", "==", ticketId)
		)
	);

	const unread = snap.docs.filter((d) => d.data().read === false);

	if (!unread.length) return;

	const batch = writeBatch(db);

	unread.forEach((d) => batch.update(d.ref, { read: true }));
	await batch.commit();
};

export const deleteNotification = async (userId, notificationId) => {
	await deleteDoc(
		doc(db, NOTIFICATIONS_COLLECTION, userId, "items", notificationId)
	);
};

export const deleteAllNotifications = async (userId) => {
	const snap = await getDocs(
		collection(db, NOTIFICATIONS_COLLECTION, userId, "items")
	);

	if (!snap.docs.length) return;

	// Firestore batches are capped at 500 ops.
	const chunks = [];

	for (let i = 0; i < snap.docs.length; i += 450) {
		chunks.push(snap.docs.slice(i, i + 450));
	}
	for (const chunk of chunks) {
		const batch = writeBatch(db);

		chunk.forEach((d) => batch.delete(d.ref));
		await batch.commit();
	}
};

// --- Visits (per-user last seen per ticket) ---

export const subscribeVisits = (userId, onUpdate, onError) => {
	if (!userId) return () => {};

	return onSnapshot(
		doc(db, VISITS_COLLECTION, userId),
		(snap) => {
			const data = snap.exists() ? snap.data() : {};

			onUpdate({
				tickets: data?.tickets ?? {},
				conversations: data?.conversations ?? {}
			});
		},
		(err) => {
			console.error("[TICKETING] subscribeVisits error", err);
			onError?.(err);
		}
	);
};

export const markTicketSeen = async (ticketId, userId) => {
	if (!userId || !ticketId) return;
	await setDoc(
		doc(db, VISITS_COLLECTION, userId),
		{ tickets: { [ticketId]: serverTimestamp() } },
		{ merge: true }
	);
};

/**
 * Records that the user has actually viewed the Conversation tab of a ticket.
 * Used to drive the "unseen messages / unseen mentions" indicators in the
 * ticket list and on the Conversation tab itself.
 */
export const markConversationSeen = async (ticketId, userId) => {
	if (!userId || !ticketId) return;
	await setDoc(
		doc(db, VISITS_COLLECTION, userId),
		{ conversations: { [ticketId]: serverTimestamp() } },
		{ merge: true }
	);
};

export const logTicketVisit = async (ticketId, currentUser) => {
	if (!ticketId || !currentUser?.id) return;
	await writeActivity(ticketId, {
		type: "visited",
		actorId: currentUser.id,
		actorName: currentUser.fullName
	});
};

/**
 * Subscribes to every devops user's "last seen on the Conversation tab"
 * timestamp for a given ticket. Used to render per-user read avatars next to
 * messages in the conversation view.
 *
 * Emits an array of { userId, seenMs }.
 */
export const subscribeTicketReaders = (ticketId, onUpdate, onError) => {
	if (!ticketId) return () => {};

	return onSnapshot(
		collection(db, VISITS_COLLECTION),
		(snap) => {
			const readers = [];

			snap.docs.forEach((d) => {
				const data = d.data();
				const v = data?.conversations?.[ticketId];

				if (!v) return;
				const ms = v.toMillis ? v.toMillis() : Number(v) || 0;

				if (!ms) return;
				readers.push({ userId: d.id, seenMs: ms });
			});
			onUpdate(readers);
		},
		(err) => {
			console.error("[TICKETING] subscribeTicketReaders error", err);
			onError?.(err);
		}
	);
};

const FOLLOW_UP_METHODS = new Set(["email", "phone", "in-person", "chat", "voicemail", "sms"]);

export const logFollowUp = async (ticketId, method, currentUser, organization = null, contact = null) => {
	if (!ticketId || !currentUser?.id) return;
	if (!FOLLOW_UP_METHODS.has(method)) {
		throw new Error(`Unknown follow-up method: ${method}`);
	}

	const entry = {
		method,
		at: new Date(),
		byUserId: currentUser.id,
		byUserName: currentUser.fullName ?? null,
		organizationId: organization?.id ?? null,
		organizationName: organization?.name ?? null,
		contactKind: contact?.kind ?? null,
		contactName: contact?.name ?? null,
		contactId: contact?.contactId ?? null
	};

	const updates = {
		lastFollowUp: entry,
		updatedAt: serverTimestamp()
	};

	if (organization?.id) {
		updates[`followUpsByOrg.${organization.id}`] = entry;
	}

	await updateDoc(doc(db, TICKETS_COLLECTION, ticketId), updates);

	await writeActivity(ticketId, {
		type: "follow-up",
		field: "follow-up",
		toValue: method,
		actorId: currentUser.id,
		actorName: currentUser.fullName,
		organizationId: organization?.id ?? null,
		organizationName: organization?.name ?? null,
		contactKind: contact?.kind ?? null,
		contactName: contact?.name ?? null
	});

	// Notify watchers + assignees (excluding the actor).
	const snap = await getDoc(doc(db, TICKETS_COLLECTION, ticketId));

	if (!snap.exists()) return;

	const t = snap.data();
	const recipients = uniqueRecipients([
		...(t.watcherIds ?? []),
		...(t.assignedDevopsIds ?? [])
	], currentUser.id);
	const actorName = currentUser.fullName ?? "Someone";
	const contactKindLabels = {
		"reporting-user": "the reporting user",
		"admin": "the admin contact",
		"tech": "the technical contact"
	};
	const contactPart = contact?.kind && contactKindLabels[contact.kind]
		? ` with ${contactKindLabels[contact.kind]}`
		: "";
	const message = `${actorName} logged a ${method} follow-up${contactPart}`;

	await Promise.all(
		recipients.map((uid) =>
			pushNotification(uid, {
				ticketId,
				ticketTitle: t.title,
				organizationName: organization?.name ?? t.organizationName,
				type: "follow_up_logged",
				field: "follow-up",
				toValue: method,
				message,
				actorId: currentUser.id,
				actorName: currentUser.fullName,
				contactKind: contact?.kind ?? null,
				contactName: contact?.name ?? null
			})
		)
	);
};

// --- Presence (who else is viewing the same ticket right now) ---

const PRESENCE_HEARTBEAT_MS = 20000;
const PRESENCE_STALE_MS = 60000;

export const joinTicketPresence = (ticketId, currentUser) => {
	if (!ticketId || !currentUser?.id) return () => {};

	const ref = doc(db, TICKETS_COLLECTION, ticketId, "viewers", currentUser.id);

	const beat = () => {
		setDoc(
			ref,
			{
				userId: currentUser.id,
				name: currentUser.fullName || "",
				at: serverTimestamp()
			},
			{ merge: true }
		).catch((err) => console.warn("[TICKETING] presence beat failed", err));
	};

	beat();
	const interval = setInterval(beat, PRESENCE_HEARTBEAT_MS);

	return () => {
		clearInterval(interval);
		deleteDoc(ref).catch(() => { /* noop */ });
	};
};

export const subscribeTicketPresence = (ticketId, onUpdate, onError) => {
	if (!ticketId) return () => {};

	return onSnapshot(
		collection(db, TICKETS_COLLECTION, ticketId, "viewers"),
		(snap) => {
			const now = Date.now();
			const viewers = snap.docs
				.map((d) => {
					const data = d.data() || {};
					const at = data.at?.toMillis ? data.at.toMillis() : 0;

					return { id: d.id, userId: data.userId || d.id, name: data.name || "", at };
				})
				.filter((v) => v.at && now - v.at < PRESENCE_STALE_MS);

			onUpdate(viewers);
		},
		(err) => {
			console.error("[TICKETING] subscribeTicketPresence error", err);
			onError?.(err);
		}
	);
};

/**
 * Subscribes to presence across every ticket so the ticket list can show
 * which devops users are currently viewing each ticket. Emits a map of
 * ticketId → array of { userId, name, at }.
 */
export const subscribeAllPresence = (onUpdate, onError) => {
	return onSnapshot(
		collectionGroup(db, "viewers"),
		(snap) => {
			const now = Date.now();
			const byTicket = {};

			snap.docs.forEach((d) => {
				// Only consider viewers docs nested directly under a support ticket.
				const ticketDoc = d.ref.parent.parent;

				if (!ticketDoc || ticketDoc.parent.id !== TICKETS_COLLECTION) return;
				const data = d.data() || {};
				const at = data.at?.toMillis ? data.at.toMillis() : 0;

				if (!at || now - at >= PRESENCE_STALE_MS) return;
				const ticketId = ticketDoc.id;

				(byTicket[ticketId] = byTicket[ticketId] || []).push({
					userId: data.userId || d.id,
					name: data.name || "",
					at
				});
			});
			onUpdate(byTicket);
		},
		(err) => {
			console.error("[TICKETING] subscribeAllPresence error", err);
			onError?.(err);
		}
	);
};

// --- Typing indicator ---

const TYPING_STALE_MS = 5000;

export const setTicketTyping = (ticketId, currentUser) => {
	if (!ticketId || !currentUser?.id) return Promise.resolve();

	const ref = doc(db, TICKETS_COLLECTION, ticketId, "typing", currentUser.id);

	return setDoc(
		ref,
		{
			userId: currentUser.id,
			name: currentUser.fullName || "",
			at: serverTimestamp()
		},
		{ merge: true }
	).catch((err) => console.warn("[TICKETING] typing beat failed", err));
};

export const clearTicketTyping = (ticketId, currentUser) => {
	if (!ticketId || !currentUser?.id) return Promise.resolve();

	const ref = doc(db, TICKETS_COLLECTION, ticketId, "typing", currentUser.id);

	return deleteDoc(ref).catch(() => { /* noop */ });
};

export const subscribeTicketTyping = (ticketId, onUpdate, onError) => {
	if (!ticketId) return () => {};

	return onSnapshot(
		collection(db, TICKETS_COLLECTION, ticketId, "typing"),
		(snap) => {
			const now = Date.now();
			const typers = snap.docs
				.map((d) => {
					const data = d.data() || {};
					const at = data.at?.toMillis ? data.at.toMillis() : 0;

					return { id: d.id, userId: data.userId || d.id, name: data.name || "", at };
				})
				.filter((t) => t.at && now - t.at < TYPING_STALE_MS);

			onUpdate(typers);
		},
		(err) => {
			console.error("[TICKETING] subscribeTicketTyping error", err);
			onError?.(err);
		}
	);
};

// --- Attachments (Firebase Storage) ---

const slugifyName = (name) =>
	(name || "file")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-zA-Z0-9._-]+/g, "_")
		.slice(0, 120);

export const uploadAttachment = async (ticketId, file) => {
	if (!file) throw new Error("No file");

	const safeName = slugifyName(file.name);
	const path = `support-tickets/${ticketId}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${safeName}`;
	const ref = storageRef(storage, path);

	await uploadBytes(ref, file, {
		contentType: file.type || "application/octet-stream"
	});
	const url = await getDownloadURL(ref);

	return {
		name: file.name,
		path,
		url,
		contentType: file.type || "application/octet-stream",
		size: file.size || 0
	};
};

export const deleteAttachment = async (path) => {
	if (!path) return;
	try {
		await deleteObject(storageRef(storage, path));
	} catch (err) {
		console.warn("[TICKETING] deleteAttachment failed", err);
	}
};

// --- Todos (per ticket) ---

export const subscribeTodos = (ticketId, onUpdate, onError) => {
	const q = query(
		collection(db, TICKETS_COLLECTION, ticketId, TODOS_SUBCOLLECTION),
		orderBy("createdAt", "asc")
	);

	return onSnapshot(
		q,
		(snap) => {
			const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

			onUpdate(items);
		},
		(err) => {
			console.error("[TICKETING] subscribeTodos error", err);
			onError?.(err);
		}
	);
};

const notifyTicketTeam = async (ticketId, currentUser, payload) => {
	const snap = await getDoc(doc(db, TICKETS_COLLECTION, ticketId));

	if (!snap.exists()) return;
	const t = snap.data();
	const recipients = uniqueRecipients([
		...(t.watcherIds ?? []),
		...(t.assignedDevopsIds ?? [])
	], currentUser?.id);

	await Promise.all(
		recipients.map((uid) =>
			pushNotification(uid, {
				ticketId,
				ticketTitle: t.title,
				organizationName: t.organizationName,
				actorId: currentUser?.id,
				actorName: currentUser?.fullName,
				...payload
			})
		)
	);
};

export const addTodo = async (ticketId, text, currentUser) => {
	const trimmed = (text || "").trim();

	if (!ticketId || !trimmed || !currentUser?.id) return null;

	const ref = await addDoc(
		collection(db, TICKETS_COLLECTION, ticketId, TODOS_SUBCOLLECTION),
		{
			text: trimmed,
			completed: false,
			createdAt: serverTimestamp(),
			createdById: currentUser.id,
			createdByName: currentUser.fullName ?? null
		}
	);

	await updateDoc(doc(db, TICKETS_COLLECTION, ticketId), {
		updatedAt: serverTimestamp()
	});

	await writeActivity(ticketId, {
		type: "todo-added",
		field: "todos",
		toValue: trimmed,
		actorId: currentUser.id,
		actorName: currentUser.fullName
	});

	const actorName = currentUser.fullName ?? "Someone";

	await notifyTicketTeam(ticketId, currentUser, {
		type: "todo_added",
		field: "todos",
		toValue: trimmed,
		message: `${actorName} added a todo: ${truncate(trimmed)}`
	});

	return ref.id;
};

export const updateTodoText = async (ticketId, todoId, text, currentUser) => {
	const trimmed = (text || "").trim();

	if (!ticketId || !todoId || !trimmed || !currentUser?.id) return;

	const todoRef = doc(db, TICKETS_COLLECTION, ticketId, TODOS_SUBCOLLECTION, todoId);
	const beforeSnap = await getDoc(todoRef);

	if (!beforeSnap.exists()) return;
	const before = beforeSnap.data();

	if (before.completed) return;
	if (before.text === trimmed) return;

	await updateDoc(todoRef, { text: trimmed });
	await updateDoc(doc(db, TICKETS_COLLECTION, ticketId), {
		updatedAt: serverTimestamp()
	});

	await writeActivity(ticketId, {
		type: "todo-edited",
		field: "todos",
		fromValue: before.text,
		toValue: trimmed,
		actorId: currentUser.id,
		actorName: currentUser.fullName
	});

	const actorName = currentUser.fullName ?? "Someone";

	await notifyTicketTeam(ticketId, currentUser, {
		type: "todo_edited",
		field: "todos",
		fromValue: before.text,
		toValue: trimmed,
		message: `${actorName} edited a todo: ${truncate(trimmed)}`
	});
};

export const setTodoCompleted = async (ticketId, todoId, completed, currentUser) => {
	if (!ticketId || !todoId || !currentUser?.id) return;

	const todoRef = doc(db, TICKETS_COLLECTION, ticketId, TODOS_SUBCOLLECTION, todoId);
	const beforeSnap = await getDoc(todoRef);

	if (!beforeSnap.exists()) return;
	const before = beforeSnap.data();

	if (!!before.completed === !!completed) return;

	const patch = completed
		? {
			completed: true,
			completedAt: serverTimestamp(),
			completedById: currentUser.id,
			completedByName: currentUser.fullName ?? null
		}
		: {
			completed: false,
			completedAt: deleteField(),
			completedById: deleteField(),
			completedByName: deleteField()
		};

	await updateDoc(todoRef, patch);
	await updateDoc(doc(db, TICKETS_COLLECTION, ticketId), {
		updatedAt: serverTimestamp()
	});

	await writeActivity(ticketId, {
		type: completed ? "todo-completed" : "todo-uncompleted",
		field: "todos",
		toValue: before.text,
		actorId: currentUser.id,
		actorName: currentUser.fullName
	});

	const actorName = currentUser.fullName ?? "Someone";

	await notifyTicketTeam(ticketId, currentUser, {
		type: completed ? "todo_completed" : "todo_uncompleted",
		field: "todos",
		toValue: before.text,
		message: completed
			? `${actorName} completed a todo: ${truncate(before.text)}`
			: `${actorName} reopened a todo: ${truncate(before.text)}`
	});
};

export const deleteTodo = async (ticketId, todoId, currentUser) => {
	if (!ticketId || !todoId || !currentUser?.id) return;

	const todoRef = doc(db, TICKETS_COLLECTION, ticketId, TODOS_SUBCOLLECTION, todoId);
	const beforeSnap = await getDoc(todoRef);

	if (!beforeSnap.exists()) return;
	const before = beforeSnap.data();
	// Completed items cannot be deleted.

	if (before.completed) return;

	await deleteDoc(todoRef);
	await updateDoc(doc(db, TICKETS_COLLECTION, ticketId), {
		updatedAt: serverTimestamp()
	});

	await writeActivity(ticketId, {
		type: "todo-deleted",
		field: "todos",
		toValue: before.text,
		actorId: currentUser.id,
		actorName: currentUser.fullName
	});

	const actorName = currentUser.fullName ?? "Someone";

	await notifyTicketTeam(ticketId, currentUser, {
		type: "todo_deleted",
		field: "todos",
		toValue: before.text,
		message: `${actorName} removed a todo: ${truncate(before.text)}`
	});
};

// --- Activity log (per ticket) ---

export const subscribeActivity = (ticketId, onUpdate, onError) => {

	const q = query(
		collection(db, TICKETS_COLLECTION, ticketId, ACTIVITY_SUBCOLLECTION),
		orderBy("createdAt", "asc")
	);

	return onSnapshot(
		q,
		(snap) => {
			const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

			onUpdate(items);
		},
		(err) => {
			console.error("[TICKETING] subscribeActivity error", err);
			onError?.(err);
		}
	);
};

const writeActivity = async (ticketId, payload) => {
	const ref = collection(db, TICKETS_COLLECTION, ticketId, ACTIVITY_SUBCOLLECTION);

	await addDoc(ref, {
		...payload,
		createdAt: serverTimestamp()
	});
};

// --- helpers ---

const uniqueRecipients = (ids, excludeId) =>
	[...new Set(ids.filter((x) => x && x !== excludeId))];

const truncate = (text, length = 80) =>
	(text?.length > length ? `${text.substring(0, length)}…` : text) ?? "";

const patchSummary = (patch) => {
	const fields = Object.keys(patch ?? {}).filter(
		(k) => !["updatedAt"].includes(k)
	);

	return fields.length ? `the ticket (${fields.join(", ")})` : "the ticket";
};

// Fields we track in the activity log, with the human-readable label sent to the UI.
const TRACKED_FIELDS = [
	{ field: "title", label: "title" },
	{ field: "description", label: "description" },
	{ field: "status", label: "status" },
	{ field: "priority", label: "priority" },
	{ field: "type", label: "type" },
	{ field: "organizationId", label: "organization", displayFrom: "organizationName", displayTo: "organizationName" },
	{ field: "assignedUserId", label: "assigned-user", displayFrom: "assignedUserName", displayTo: "assignedUserName" },
	{ field: "assignedDevopsIds", label: "assigned-devops", displayFrom: "assignedDevopsNames", displayTo: "assignedDevopsNames", isArray: true },
	{ field: "dueAt", label: "due-date" },
	{ field: "attachments", label: "attachments", isAttachmentList: true }
];

const computeFieldDiffs = (before, patch) => {
	const diffs = [];

	for (const spec of TRACKED_FIELDS) {
		if (!Object.prototype.hasOwnProperty.call(patch, spec.field)) continue;

		const newVal = patch[spec.field];
		const oldVal = before?.[spec.field];

		if (spec.isAttachmentList) {
			const before = (oldVal ?? []).map((x) => x?.name).filter(Boolean);
			const after = (newVal ?? []).map((x) => x?.name).filter(Boolean);
			const added = after.filter((n) => !before.includes(n));
			const removed = before.filter((n) => !after.includes(n));

			if (added.length) {
				diffs.push({ type: "attachments-added", field: spec.label, from: null, to: added.join(", ") });
			}
			if (removed.length) {
				diffs.push({ type: "attachments-removed", field: spec.label, from: null, to: removed.join(", ") });
			}
			continue;
		}

		if (spec.isArray) {
			const a = [...(oldVal ?? [])].sort();
			const b = [...(newVal ?? [])].sort();

			if (a.length === b.length && a.every((v, i) => v === b[i])) continue;
		} else if (oldVal === newVal) {
			continue;
		} else if (oldVal == null && newVal == null) {
			continue;
		}

		const from = spec.displayFrom ? before?.[spec.displayFrom] ?? oldVal : oldVal;
		const to = spec.displayTo
			? (Object.prototype.hasOwnProperty.call(patch, spec.displayTo)
				? patch[spec.displayTo]
				: before?.[spec.displayTo]) ?? newVal
			: newVal;

		diffs.push({ field: spec.label, from: serializeValue(from), to: serializeValue(to) });
	}

	return diffs;
};

const serializeValue = (v) => {
	if (v == null) return null;
	if (Array.isArray(v)) return v.join(", ");
	if (typeof v === "object") return JSON.stringify(v);
	return String(v);
};

// --- Support contacts (per organization) ---

export const subscribeOrgContacts = (organizationId, onUpdate, onError) => {
	if (!organizationId) {
		onUpdate(null);
		return () => {};
	}

	return onSnapshot(
		doc(db, CONTACTS_COLLECTION, organizationId),
		(snap) => {
			onUpdate(snap.exists() ? { id: snap.id, ...snap.data() } : null);
		},
		(err) => {
			console.error("[TICKETING] subscribeOrgContacts error", err);
			onError?.(err);
		}
	);
};

export const saveOrgContacts = async (organizationId, { admins = [], technicians = []}, currentUser) => {
	if (!organizationId) throw new Error("organizationId required");

	const ref = doc(db, CONTACTS_COLLECTION, organizationId);

	await setDoc(ref, {
		admins,
		technicians,
		updatedAt: serverTimestamp(),
		updatedById: currentUser?.id ?? null,
		updatedByName: currentUser?.fullName ?? null
	}, { merge: true });
};

export const subscribeAllOrgContacts = (onUpdate, onError) => {
	return onSnapshot(
		collection(db, CONTACTS_COLLECTION),
		(snap) => {
			const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

			onUpdate(docs);
		},
		(err) => {
			console.error("[TICKETING] subscribeAllOrgContacts error", err);
			onError?.(err);
		}
	);
};
